import { useState, useEffect, ChangeEvent } from 'react';

import { OS_TYPE } from '@common/const';
import { onlyNumber } from '@common/regExp';
import {
  checkValidAccountNumber,
  checkValidKakaoId,
  checkValidPhoneNumber,
  validEmail as checkValidEmail,
} from '@common/validate';
import { isAppDevice } from '@common/webview';
import { FraudResultType, SearchType } from '@type/web/fraud';

import * as FraudAPI from '@api/base/fraud';

const SEARCH_TYPE_TABLE = {
  PHONE_NUMBER: 'phone_number',
  ACCOUNT_NUMBER: 'account_number',
  KAKAO_ID: 'kakao_id',
  EMAIL: 'email',
};

const VALID_RULES = {
  PHONE_NUMBER: checkValidPhoneNumber,
  ACCOUNT_NUMBER: checkValidAccountNumber,
  KAKAO_ID: checkValidKakaoId,
  EMAIL: checkValidEmail,
};

interface UseFraudSearchFormProps {
  type: SearchType;
  isValid: boolean;
  inputValue: string;
  selectType: (newType: SearchType) => void;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  getFraudSearchResult: (
    inputValue: string,
    type: SearchType,
  ) => Promise<FraudResultType | null>;
}

const useFraudSearchForm = (): UseFraudSearchFormProps => {
  const [inputValue, setInputValue] = useState('');
  const [type, setType] = useState<SearchType>('PHONE_NUMBER');
  const [isValid, setIsValid] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const validValue =
      type === 'PHONE_NUMBER' || type === 'ACCOUNT_NUMBER'
        ? onlyNumber(value)
        : value;
    setInputValue(validValue);
  };

  const handleValidRules = (fieldType: SearchType, value: string) =>
    VALID_RULES[fieldType](value);

  useEffect(() => {
    if (inputValue === '') setIsValid(false);
    else setIsValid(handleValidRules(type, inputValue));
  }, [inputValue, type]);

  const selectType = (newType: SearchType) => {
    setType(newType);
  };

  /** 사기조회 api 요청 */
  const getFraudSearchResult = async (
    inputValue: string,
    type: SearchType,
  ): Promise<FraudResultType | null> => {
    const osType = isAppDevice() ? OS_TYPE.APP : OS_TYPE.WEB; // TODO: osType 판단 로직 추가

    try {
      const { data } = await FraudAPI.getFraudLookup({
        keyword: inputValue,
        type: SEARCH_TYPE_TABLE[type],
        osType,
      });
      return data;
    } catch (e) {
      console.dir(`사기조회 api 호출 에러: ${e}`);
      throw e;
    }
  };

  return {
    type,
    isValid,
    inputValue,
    selectType,
    handleChange,
    getFraudSearchResult,
  };
};

export default useFraudSearchForm;
